import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { getRoleBadgeClass, useViewportKey } from '../pages/ProjectsPageHelpers';
import '../pages/ProjectsPage.css';
import { ProjectWithDetails, projectService } from '../services/projectService';
import { fetchProjectScreenshots } from '../services/screenshotService';
import { tokenService } from '../services/tokenService';
import { isIOSApp } from '../utils/platformDetection';

import { usePendingInvite } from './PendingInviteModal';
import { ProjectVisibilityDropdown } from './ProjectVisibilityDropdown';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface ProjectListProps {
  isAuthenticated: boolean;
  authIsLoading?: boolean;
}

type ViewportKey = 'desktop' | 'tablet' | 'mobile';

interface ResponsiveScreenshotAsset {
  full?: string;
  thumb?: string;
  video?: string;
}

interface ScreenshotThumb {
  page_id: string;
  page_name: string;
  screenshot_thumb_url: string;
  video_generation_status?: string;
  responsive_screenshots?: {
    [viewport: string]: ResponsiveScreenshotAsset;
  };
}

/* ── Build-time preloaded data (injected by scripts/prerender_static.cjs) ──
   Seeding initial state from these makes the first client render identical to
   the prerendered HTML, so hydration never swaps visible content. */

interface PreloadedWindow {
  __PRELOADED_EXPLORE__?: { items?: PublicProject[] };
  __PRELOADED_SCREENSHOTS__?: { [projectId: string]: ScreenshotThumb[] };
}

const preloaded = (typeof window !== 'undefined' ? window : {}) as PreloadedWindow;

/* Revalidation behind preloaded data is not on the critical path: the markup it
   would produce is already on screen. Deferring it past the first paint keeps
   hydration from competing with a round trip whose result is usually identical.
   Without preloaded data the fetch still runs immediately — it is the only way
   to get any content at all. */
const POST_PAINT_TIMEOUT_MS = 500;

type IdleWindow = typeof globalThis & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

const schedulePostPaint = (run: () => void): (() => void) => {
  const w = window as IdleWindow;

  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(run, { timeout: POST_PAINT_TIMEOUT_MS });

    return () => w.cancelIdleCallback?.(id);
  }

  const id = window.setTimeout(run, 0);

  return () => window.clearTimeout(id);
};

/* Revalidation usually returns exactly what is already rendered. Committing it
   anyway swaps in fresh object identities and re-renders every card — new <img>
   and <video> elements for identical URLs. Comparing first keeps the DOM still
   when nothing changed. */
const isSameData = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/* Preloaded data is already painted, so refreshing it waits for idle time.
   Without it the fetch is the only source of content and has to run now.
   Returns a canceller for the pending schedule. */
const startRevalidation = (deferred: boolean, run: () => void): (() => void) => {
  if (deferred) return schedulePostPaint(run);

  run();

  return () => undefined;
};

/* ── Skeleton ──────────────────────────────────────── */

const ArrowIconHidden: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ visibility: 'hidden' }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* CardSkeleton mirrors the shared geometry of LandingProjectCard and
   PublicProjectCard. The authenticated version also reserves the owner visibility
   control so loading into the most common card state does not shift the footer. */
const CardSkeleton: React.FC<{ publicView?: boolean }> = ({ publicView = false }) => (
  <div className={`project-card project-card--landing project-card--skeleton ${publicView ? 'project-card--landing-public' : ''}`} aria-hidden="true">
    <div className="project-card-screenshot project-card-screenshot--desktop">
      <div className="project-card-screenshot-frame project-card-skeleton-block">
        <div className="project-card-screenshot-stack">
          <svg viewBox="0 0 16 9" style={{ width: '100%', display: 'block', visibility: 'hidden' }} />
        </div>
        <span className="project-card-screenshot-label z-20 relative" style={{ color: 'transparent', background: 'transparent' }}>
          <span className="project-card-skeleton-block">Preview</span>
        </span>
      </div>
    </div>
    <div className="project-card-header">
      <div className="project-card-title-group">
        <h3 className="project-card-title" style={{ color: 'transparent' }}>
          <span className="project-card-skeleton-block">Untitled Project</span>
        </h3>
      </div>
      {publicView ? (
        <span className="project-card-owner-badge" style={{ color: 'transparent' }}>
          <span className="project-card-skeleton-block">by</span>{' '}
          <svg className="owner-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ visibility: 'hidden' }}>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
          </svg>{' '}
          <span className="project-card-skeleton-block">Owner</span>
        </span>
      ) : (
        <span className="role-badge project-card-skeleton-block">
          Owner
        </span>
      )}
    </div>
    <div className="project-card-footer">
      <div className="project-card-actions">
        {!publicView && <span className="project-card-visibility-skeleton project-card-skeleton-block" />}
        <span className="project-card-open project-card-skeleton-block">
          Open <ArrowIconHidden />
        </span>
      </div>
    </div>
  </div>
);

const ProjectListSkeleton: React.FC<{ publicView?: boolean }> = ({ publicView = false }) => {
  const showPublicProjectsLink = !isIOSApp();
  return (
    <div className="project-list">
      <div className="project-list__heading-row">
        <h2 className="project-list__heading">{publicView ? 'Public Projects' : 'My Projects'}</h2>
        {((publicView) || (!publicView && showPublicProjectsLink)) && (
          <span className="project-list__public-link project-card-skeleton-block" style={{ color: 'transparent', pointerEvents: 'none' }}>
            {publicView ? 'Explore All →' : 'Public Projects →'}
          </span>
        )}
      </div>
      <div className="project-list__grid">
        {[1, 2, 3].map((i) => <CardSkeleton key={i} publicView={publicView} />)}
      </div>
    </div>
  );
};

/* ── Pure CSS scroll — hover animation (from ProjectsGrid) ── */

const CONTAINER_H = 200;
const SCROLL_PX_PER_S = 300;
const VIDEO_START_TRIM_S = 0;
const VIDEO_END_TRIM_S = 0;

/* An Extreme landing preview is a 10 s scroll of the whole page; the browser plays it
   at a third of the speed so it can be followed on a card (nothing is re-encoded). */
export const EXTREME_PREVIEW_PLAYBACK_RATE = 1 / 3;

function getImgH(img: HTMLImageElement, cw: number, fallbackHeight: number): number {
  return ((img.naturalHeight || fallbackHeight) / (img.naturalWidth || 640)) * cw;
}

function computeOffsets(imgs: HTMLImageElement[], cw: number, fallbackHeight: number): number[] {
  let cum = 0;

  return imgs.map((img) => {
    const off = cum;

    cum += getImgH(img, cw, fallbackHeight);

    return off;
  });
}

function findPage(y: number, offsets: number[], containerHeight: number): number {
  for (let i = offsets.length - 1; i >= 0; i -= 1) {
    if (y >= offsets[i] - containerHeight / 2) return i;
  }

  return 0;
}

function updateDotsDOM(container: HTMLDivElement, pageIdx: number, names: string[]) {
  const dots = container.querySelectorAll('.screenshot-dot');

  dots.forEach((dot, i) => {
    dot.classList.toggle('active', i === pageIdx);
  });

  const lbl = container.querySelector('.project-card-screenshot-label');

  if (lbl) lbl.textContent = names[pageIdx] || names[0];
}

function readTransformY(el: HTMLDivElement): number {
  const m = window.getComputedStyle(el).transform;

  if (!m || m === 'none') return 0;

  const parts = m.match(/matrix\(([^)]+)\)/);

  if (!parts) return 0;

  const vals = parts[1].split(',');

  return Math.abs(parseFloat(vals[5] || '0'));
}

const LOOP_PAUSE_MS = 1500;

interface ScrollState {
  rafId: number | null;
  stopped: boolean;
  loopTimer: ReturnType<typeof setTimeout> | null;
  transitionHandler: (() => void) | null;
}

function trackDots(
  container: HTMLDivElement,
  stack: HTMLDivElement,
  offsets: number[],
  names: string[],
  state: ScrollState,
  containerHeight: number,
) {
  let lastPage = -1;

  function tick() {
    if (state.stopped) return;

    const y = readTransformY(stack);
    const page = findPage(y, offsets, containerHeight);

    if (page !== lastPage) { lastPage = page; updateDotsDOM(container, page, names); }

    state.rafId = window.requestAnimationFrame(tick);
  }

  state.rafId = window.requestAnimationFrame(tick);
}

function getScrollMetrics(stack: HTMLDivElement, cw: number, containerHeight: number) {
  const imgs = Array.from(stack.querySelectorAll('img')) as HTMLImageElement[];
  const offsets = computeOffsets(imgs, cw, containerHeight);
  const lastH = imgs.length > 0 ? getImgH(imgs[imgs.length - 1], cw, containerHeight) : 0;
  const totalH = (offsets[offsets.length - 1] || 0) + lastH;

  return { offsets, overflow: totalH - containerHeight };
}

function applyCssTransition(stack: HTMLDivElement, overflow: number) {
  stack.style.transition = `transform ${overflow / SCROLL_PX_PER_S}s linear`;
  stack.style.transform = `translateY(-${overflow}px)`;
}

function stopScroll(stack: HTMLDivElement, state: ScrollState | null) {
  if (state) {
    state.stopped = true;
    if (state.rafId) window.cancelAnimationFrame(state.rafId);
    if (state.loopTimer) clearTimeout(state.loopTimer);
    if (state.transitionHandler) stack.removeEventListener('transitionend', state.transitionHandler);
  }

  stack.style.transition = 'transform 0.3s ease-out';
  stack.style.transform = 'translate3d(0, 0, 0)';
}

function startCssScroll(
  container: HTMLDivElement,
  stack: HTMLDivElement,
  names: string[],
): ScrollState {
  const containerHeight = container.clientHeight || CONTAINER_H;
  const { offsets, overflow } = getScrollMetrics(stack, container.clientWidth, containerHeight);
  const state: ScrollState = { rafId: null, stopped: false, loopTimer: null, transitionHandler: null };

  if (overflow <= 0) return state;

  const beginScroll = () => {
    if (state.stopped) return;

    applyCssTransition(stack, overflow);
    trackDots(container, stack, offsets, names, state, containerHeight);
  };

  const onTransitionEnd = () => {
    if (state.stopped) return;

    // Stop dot tracking for the pause
    if (state.rafId) { window.cancelAnimationFrame(state.rafId); state.rafId = null; }

    // Instant reset to top
    stack.style.transition = 'none';
    stack.style.transform = 'translate3d(0, 0, 0)';
    updateDotsDOM(container, 0, names);

    // Pause at top, then scroll again
    state.loopTimer = setTimeout(() => {
      if (state.stopped) return;

      // Force reflow so the reset takes effect before starting new transition
      void stack.offsetHeight;
      beginScroll();
    }, LOOP_PAUSE_MS);
  };

  state.transitionHandler = onTransitionEnd;
  stack.addEventListener('transitionend', onTransitionEnd);

  beginScroll();

  return state;
}

function handleScrollEffect(
  container: HTMLDivElement,
  stack: HTMLDivElement,
  isHovered: boolean,
  names: string[],
): (() => void) | undefined {
  if (!isHovered) {
    stopScroll(stack, null);

    return undefined;
  }

  const state = startCssScroll(container, stack, names);

  return () => stopScroll(stack, state);
}

function useCssScroll(
  isHovered: boolean,
  containerRef: React.RefObject<HTMLDivElement | null>,
  stackRef: React.RefObject<HTMLDivElement | null>,
  names: string[],
) {
  useEffect(() => {
    const container = containerRef.current;
    const stack = stackRef.current;

    if (!container || !stack) return undefined;

    return handleScrollEffect(container, stack, isHovered, names);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, names.length]);
}

/* ── Screenshot sub-components & hook ──────────────── */

function useScreenshotFetch(projectId: string) {
  const preloadedShots = preloaded.__PRELOADED_SCREENSHOTS__?.[projectId];
  const [screenshots, setScreenshots] = useState<ScreenshotThumb[]>(preloadedShots ?? []);
  const [loaded, setLoaded] = useState(!!preloadedShots);

  useEffect(() => {
    let cancelled = false;

    const fetchScreenshots = () => {
      fetchProjectScreenshots(projectId).then(
        (data) => {
          if (cancelled) return;

          setScreenshots((prev) => (isSameData(prev, data) ? prev : data));
          setLoaded(true);
        },
        () => {
          if (!cancelled) {
            setLoaded(true);
          }
        },
      );
    };

    const cancelSchedule = startRevalidation(!!preloadedShots, fetchScreenshots);

    return () => {
      cancelled = true;
      cancelSchedule();
    };
    // `preloadedShots` is read from a build-time global and is stable per project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return { screenshots, loaded };
}

const EmptyScreenshot: React.FC = () => (
  <div className="project-card-screenshot project-card-screenshot--empty">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
    <span>No preview</span>
  </div>
);

const buildAssetUrl = (key: string) => (
  `${API_BASE_URL}/api/v1/projects/design-screenshots/${key}`
);

const getViewportThumb = (screenshot: ScreenshotThumb, viewportKey: ViewportKey): string => (
  screenshot.responsive_screenshots?.[viewportKey]?.thumb || screenshot.screenshot_thumb_url
);

const getStrictViewportThumb = (screenshot: ScreenshotThumb, viewportKey: ViewportKey): string | undefined => (
  viewportKey === 'desktop'
    ? getViewportThumb(screenshot, viewportKey)
    : screenshot.responsive_screenshots?.[viewportKey]?.thumb
);

const screenshotsHaveViewportThumbs = (screenshots: ScreenshotThumb[], viewportKey: ViewportKey): boolean => (
  viewportKey === 'desktop' || screenshots.every((screenshot) => !!getStrictViewportThumb(screenshot, viewportKey))
);

const getViewportVideo = (
  screenshot: ScreenshotThumb,
  viewportKey: ViewportKey,
  allowDesktopFallback: boolean,
): string | undefined => (
  screenshot.responsive_screenshots?.[viewportKey]?.video
  || (allowDesktopFallback ? screenshot.responsive_screenshots?.desktop?.video : undefined)
);

const getViewportKeyForIndex = (index: number | undefined, projectId: string): ViewportKey => {
  if (index !== undefined) {
    const mod = index % 12;
    if (mod === 2 || mod === 5 || mod === 9) return 'mobile';
    return 'desktop';
  }
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = projectId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 100 < 80 ? 'desktop' : 'mobile';
};

const ScreenshotImages: React.FC<{
  screenshots: ScreenshotThumb[];
  videoPages: ScreenshotThumb[];
  stackRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  isHovered: boolean;
  videoStackRef: React.RefObject<HTMLDivElement | null>;
  videosRef: React.MutableRefObject<(HTMLVideoElement | null)[]>;
  viewportKey: ViewportKey;
  frameViewportKey: ViewportKey;
  allowDesktopVideoFallback: boolean;
  autoPlayVideo?: boolean;
}> = ({
  screenshots,
  videoPages,
  stackRef,
  containerRef,
  isHovered,
  videoStackRef,
  videosRef,
  viewportKey,
  frameViewportKey,
  allowDesktopVideoFallback,
  autoPlayVideo,
}) => {
  const firstS = screenshots[0];
  const hasVideo = videoPages.length > 0;
  const dotPages = hasVideo ? videoPages : screenshots;
  const showVideo = autoPlayVideo || isHovered;

  return (
    <div className={`project-card-screenshot project-card-screenshot--${frameViewportKey}`}>
      <div className="project-card-screenshot-frame" ref={containerRef}>
        {hasVideo && (
          <div
            className="project-card-screenshot-video-stack"
            ref={videoStackRef}
            style={{ opacity: showVideo ? 1 : 0 }}
          >
            {videoPages.map((p, i) => {
              const url = getViewportVideo(p, viewportKey, allowDesktopVideoFallback);
              const posterKey = getViewportThumb(p, viewportKey);

              return url ? (
                <video
                  key={p.page_id}
                  ref={(el) => { videosRef.current[i] = el; }}
                  src={buildAssetUrl(url)}
                  poster={buildAssetUrl(posterKey)}
                  muted
                  loop={videoPages.length === 1}
                  playsInline
                  preload={autoPlayVideo ? 'auto' : 'none'}
                  className="project-card-screenshot-video"
                />
              ) : null;
            })}
          </div>
        )}
        <div
          className="project-card-screenshot-stack"
          style={{ opacity: hasVideo && showVideo ? 0 : 1 }}
          ref={stackRef}
        >
          {screenshots.map((s) => (
            <img
              key={s.page_id}
              src={buildAssetUrl(getViewportThumb(s, viewportKey))}
              alt={s.page_name}
              className="project-card-screenshot-img"
              loading="lazy"
            />
          ))}
        </div>
        {dotPages.length > 1 && (
          <div className="project-card-screenshot-dots z-20 relative">
            {dotPages.map((s, i) => (
              <span key={s.page_id} className={`screenshot-dot ${i === 0 ? 'active' : ''}`} />
            ))}
          </div>
        )}
        <span className="project-card-screenshot-label z-20 relative">{firstS.page_name}</span>
      </div>
    </div>
  );
};

/* ── Screenshot Preview (with hover scroll animation) ── */

export const ScreenshotPreview: React.FC<{
  projectId: string;
  isHovered: boolean;
  randomizeViewport?: boolean;
  autoPlayVideo?: boolean;
  desktopOnly?: boolean;
  index?: number;
  /** Playback rate for the project's landing page clip only (its first page). */
  landingPlaybackRate?: number;
}> = ({ projectId, isHovered, randomizeViewport, autoPlayVideo, desktopOnly, index, landingPlaybackRate }) => {
  const { screenshots, loaded } = useScreenshotFetch(projectId);
  const stackRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoStackRef = useRef<HTMLDivElement | null>(null);
  const videosRef = useRef<(HTMLVideoElement | null)[]>([]);

  const defaultViewportKey = useViewportKey();

  const requestedViewportKey = React.useMemo<ViewportKey>(() => {
    if (desktopOnly) return 'desktop';
    if (!randomizeViewport) return defaultViewportKey;

    return getViewportKeyForIndex(index, projectId);
  }, [projectId, randomizeViewport, defaultViewportKey, desktopOnly, index]);

  const viewportKey = React.useMemo<ViewportKey>(() => (
    !desktopOnly && randomizeViewport && !screenshotsHaveViewportThumbs(screenshots, requestedViewportKey)
      ? 'desktop'
      : requestedViewportKey
  ), [desktopOnly, randomizeViewport, requestedViewportKey, screenshots]);
  const allowDesktopVideoFallback = desktopOnly || !randomizeViewport || viewportKey === 'desktop';
  const frameViewportKey: ViewportKey = randomizeViewport && !desktopOnly ? viewportKey : 'desktop';

  const videoPages = screenshots.filter((s) => {
    const url = getViewportVideo(s, viewportKey, allowDesktopVideoFallback);

    return s.video_generation_status === 'completed' && !!url;
  });
  const hasVideo = videoPages.length > 0;
  const videoCount = videoPages.length;
  const pageNames = videoPages.map((p) => p.page_name);
  // A new src reloads a <video> and leaves it paused. Prerendered cards start from the
  // build-time URL and revalidate to the current one after a video is regenerated, so
  // the play effect below must run again whenever the URLs change.
  const videoSrcKey = videoPages.map((p) => getViewportVideo(p, viewportKey, allowDesktopVideoFallback)).join('|');

  const effectiveHovered = autoPlayVideo || isHovered;

  useCssScroll(!hasVideo && effectiveHovered, containerRef, stackRef, screenshots.map((s) => s.page_name));

  useEffect(() => {
    const container = containerRef.current;
    const stack = videoStackRef.current;

    if (!container || !stack || videoCount === 0) return undefined;

    let cancelled = false;

    const updateIndicators = (idx: number) => {
      const lbl = container.querySelector('.project-card-screenshot-label');

      if (lbl) lbl.textContent = pageNames[idx] || pageNames[0] || '';

      const dots = container.querySelectorAll('.screenshot-dot');

      dots.forEach((d, i) => d.classList.toggle('active', i === idx));
    };

    const seekStart = (v: HTMLVideoElement) => {
      const d = v.duration;
      const start = Number.isFinite(d) && d > VIDEO_START_TRIM_S + VIDEO_END_TRIM_S ? VIDEO_START_TRIM_S : 0;

      try { v.currentTime = start; } catch { /* metadata not yet loaded */ }
    };

    const startPlayback = (v: HTMLVideoElement, idx: number) => {
      const isLanding = videoPages[idx]?.page_id === screenshots[0]?.page_id;
      const rate = landingPlaybackRate && isLanding ? landingPlaybackRate : 1;

      v.defaultPlaybackRate = rate; // survives the reload that a new src triggers
      v.playbackRate = rate;
      v.play().catch(() => {});
    };

    const playAt = (idx: number) => {
      if (cancelled) return;

      const h = container.clientHeight;

      stack.style.transform = `translate3d(0, -${idx * h}px, 0)`;
      updateIndicators(idx);

      const v = videosRef.current[idx];

      if (v) {
        seekStart(v);
        startPlayback(v, idx);
      }
    };

    if (effectiveHovered) {
      if (videoCount === 1) {
        const v = videosRef.current[0];

        if (v) {
          try { v.currentTime = 0; } catch { /* metadata not yet loaded */ }
          startPlayback(v, 0);
        }
      } else {
        let activeIdx = 0;

        const advance = (fromIdx: number) => {
          if (cancelled || fromIdx !== activeIdx) return;

          const prev = videosRef.current[fromIdx];

          if (prev) prev.pause();

          activeIdx = (fromIdx + 1) % videoCount;
          playAt(activeIdx);
        };

        videosRef.current.slice(0, videoCount).forEach((v, i) => {
          if (!v) return;

          v.ontimeupdate = () => {
            if (i !== activeIdx) return;

            const d = v.duration;

            if (!Number.isFinite(d) || d <= VIDEO_START_TRIM_S + VIDEO_END_TRIM_S) return;
            if (v.currentTime >= d - VIDEO_END_TRIM_S) advance(i);
          };
          v.onended = () => advance(i);
        });

        playAt(0);
      }
    } else {
      stack.style.transform = 'translate3d(0, 0, 0)';

      videosRef.current.forEach((v) => {
        if (!v) return;

        v.pause();
        try { v.currentTime = 0; } catch { /* metadata not yet loaded */ }
        v.onended = null;
        v.ontimeupdate = null;
      });
    }

    return () => {
      cancelled = true;

      videosRef.current.forEach((v) => {
        if (!v) return;

        v.onended = null;
        v.ontimeupdate = null;
        v.pause();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveHovered, videoCount, viewportKey, allowDesktopVideoFallback, videoSrcKey, landingPlaybackRate]);

  if (!loaded || screenshots.length === 0) return <EmptyScreenshot />;

  return (
    <ScreenshotImages
      screenshots={screenshots}
      videoPages={videoPages}
      stackRef={stackRef}
      containerRef={containerRef}
      isHovered={isHovered}
      videoStackRef={videoStackRef}
      videosRef={videosRef}
      viewportKey={viewportKey}
      frameViewportKey={frameViewportKey}
      allowDesktopVideoFallback={allowDesktopVideoFallback}
      autoPlayVideo={autoPlayVideo}
    />
  );
};

/* ── Icons ─────────────────────────────────────────── */

const ArrowIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* ── Project Card (read-only — no checkbox/delete) ── */

const LandingProjectCard: React.FC<{
  project: ProjectWithDetails;
  onClick: (id: string) => void;
  autoPlayVideo?: boolean;
  onProjectVisibilityChange: (projectId: string | null, isPublic: boolean) => void;
}> = ({ project, onClick, autoPlayVideo = false, onProjectVisibilityChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const invite = usePendingInvite(project, onClick);
  const { isPending } = invite;
  const roleLabel = isPending ? 'Invite pending' : project.role.charAt(0).toUpperCase() + project.role.slice(1);

  if (invite.declined) return null;

  return (
    <div
      className={`project-card project-card--landing ${isPending ? 'project-card--pending' : ''}`}
      onClick={invite.onActivate}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
      onTouchCancel={() => setIsHovered(false)}
    >
      <ScreenshotPreview
        projectId={project.id}
        isHovered={isHovered}
        autoPlayVideo={autoPlayVideo || Boolean(project.is_extreme_design)}
        landingPlaybackRate={project.is_extreme_design ? EXTREME_PREVIEW_PLAYBACK_RATE : undefined}
        desktopOnly
      />
      {project.is_extreme_design && <span className="project-card-extreme-ribbon"><span>Extreme</span></span>}
      <div className="project-card-header">
        <div className="project-card-title-group">
          <h3 className="project-card-title">{project.title || 'Untitled Project'}</h3>
        </div>
        <span className={`role-badge ${isPending ? 'role-badge--pending' : getRoleBadgeClass(project.role)}`}>
          {roleLabel}
        </span>
      </div>
      <div className="project-card-footer">
        <div className="project-card-actions">
          {!isPending && project.is_owner && (
            <ProjectVisibilityDropdown
              projectId={project.id}
              isPublic={project.is_public}
              onVisibilityChange={onProjectVisibilityChange}
            />
          )}
          {!isPending && (
            <a className="project-card-open" href={`/planning/${project.id}`} onClick={(e) => e.stopPropagation()}>
              Open <ArrowIcon />
            </a>
          )}
        </div>
      </div>
      {invite.replyWindow}
    </div>
  );
};

/* ── Hook ──────────────────────────────────────────── */

const MAX_VISIBLE = 3;

const useProjectList = (isAuthenticated: boolean) => {
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const fetchProjects = async () => {
      try {
        const data = await projectService.getProjectsWithDetails(1, MAX_VISIBLE);

        if (!cancelled) {
          setProjects(data.items);
          setTotalCount(data.total);
        }
      } catch {
        if (!cancelled) {
          setProjects([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProjects();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  return { projects, setProjects, totalCount, loading, navigate };
};

/* ── Public projects hook (for non-authenticated users) ── */

interface PublicProject {
  id: string;
  title: string;
  first_message: string | null;
  status: string;
  owner_name: string | null;
  owner_image_url: string | null;
  is_extreme_design?: boolean;
  votes: { up: number; down: number };
  created_at: string | null;
  updated_at: string | null;
}

const usePublicProjects = (isAuthenticated: boolean) => {
  const preloadedItems = preloaded.__PRELOADED_EXPLORE__?.items?.slice(0, MAX_VISIBLE);
  const hasPreloaded = !!preloadedItems?.length;
  const [projects, setProjects] = useState<PublicProject[]>(preloadedItems ?? []);
  const [loading, setLoading] = useState(!hasPreloaded);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) return undefined;

    let cancelled = false;

    const fetchExplore = () => {
      projectService.exploreProjects(1, MAX_VISIBLE).then(
        (data) => {
          if (cancelled) return;

          const next = data.items.slice(0, MAX_VISIBLE);

          setProjects((prev) => (isSameData(prev, next) ? prev : next));
          setLoading(false);
        },
        () => { if (!cancelled) setLoading(false); },
      );
    };

    const cancelSchedule = startRevalidation(hasPreloaded, fetchExplore);

    return () => {
      cancelled = true;
      cancelSchedule();
    };
    // `hasPreloaded` comes from a build-time global and never changes at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { projects, loading, navigate };
};

const OwnerBadge: React.FC<{ name: string | null; imageUrl: string | null }> = ({ name, imageUrl }) => {
  if (!name) return null;

  return (
    <span className="project-card-owner-badge">
      by{' '}
      {imageUrl
        ? <img src={`${API_BASE_URL}${imageUrl}`} alt="" className="owner-badge-avatar" />
        : (
          <svg className="owner-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
          </svg>
        )}
      {name}
    </span>
  );
};

/* ── Public project card (for non-authenticated view) ── */

const PublicProjectCard: React.FC<{
  project: PublicProject;
  onClick: (id: string) => void;
  autoPlayVideo?: boolean;
}> = ({ project, onClick, autoPlayVideo = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="project-card project-card--landing project-card--landing-public"
      onClick={() => onClick(project.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
      onTouchCancel={() => setIsHovered(false)}
    >
      <ScreenshotPreview
        projectId={project.id}
        isHovered={isHovered}
        autoPlayVideo={autoPlayVideo || Boolean(project.is_extreme_design)}
        landingPlaybackRate={project.is_extreme_design ? EXTREME_PREVIEW_PLAYBACK_RATE : undefined}
        desktopOnly
      />
      {project.is_extreme_design && <span className="project-card-extreme-ribbon"><span>Extreme</span></span>}
      <div className="project-card-header">
        <div className="project-card-title-group">
          <h3 className="project-card-title">{project.title || 'Untitled Project'}</h3>
        </div>
        <OwnerBadge name={project.owner_name} imageUrl={project.owner_image_url} />
      </div>
      <div className="project-card-footer">
        <div className="project-card-actions">
          <a className="project-card-open" href={`/projects/${project.title || project.id}`} onClick={(e) => e.stopPropagation()}>
            Open <ArrowIcon />
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ────────────────────────────────── */

const PublicProjectListView: React.FC = () => {
  const { projects, loading, navigate } = usePublicProjects(false);

  // Capture snapshot HTML before React can destroy it (runs once, during initial render).
  // The skeleton must be rejected, or a prerendered skeleton grid gets frozen
  // into the page as if it were real content — it is marked by its own class,
  // not by an inline "pulse" style, which the previous check looked for.
  const [snapshotHtml] = useState<string | null>(() => {
    if (typeof document === 'undefined') return null;
    const grid = document.querySelector('.project-list__grid');
    if (grid && grid.querySelector('.project-card') && !grid.querySelector('.project-card--skeleton')) {
      return grid.outerHTML;
    }
    return null;
  });

  // While loading: show the pre-rendered snapshot if available, otherwise skeleton
  if (loading) {
    if (snapshotHtml) {
      return (
        <div className="project-list">
          <div className="project-list__heading-row">
            <h2 className="project-list__heading">Public Projects</h2>
            <Link className="project-list__public-link" to="/explore">
              Explore All →
            </Link>
          </div>
          <div dangerouslySetInnerHTML={{ __html: snapshotHtml }} suppressHydrationWarning />
        </div>
      );
    }
    return <ProjectListSkeleton publicView />;
  }
  if (projects.length === 0) {
    return (
      <div className="project-list" style={{ textAlign: 'center' }}>
        <Link className="project-list__why-us" to="/about">
          Why us? →
        </Link>
      </div>
    );
  }

  return (
    <div className="project-list">
      <div className="project-list__heading-row">
        <h2 className="project-list__heading">Public Projects</h2>
        <Link className="project-list__public-link" to="/explore">
          Explore All →
        </Link>
      </div>
      <div className="project-list__grid">
        {projects.map((p, index) => (
          <PublicProjectCard
            key={p.id}
            project={p}
            onClick={() => navigate(`/projects/${p.title || p.id}`)}
            autoPlayVideo={index === 1}
          />
        ))}
      </div>
    </div>
  );
};

const AuthenticatedProjectListView: React.FC = () => {
  const { projects, setProjects, totalCount, loading, navigate } = useProjectList(true);
  const showPublicProjectsLink = !isIOSApp();
  const handleProjectVisibilityChange = useCallback((projectId: string | null, isPublic: boolean) => {
    setProjects((prev) => prev.map((project) => (
      project.id === projectId ? { ...project, is_public: isPublic } : project
    )));
  }, [setProjects]);

  if (loading) return <ProjectListSkeleton />;
  if (projects.length === 0) return null;

  return (
    <div className="project-list">
      <div className="project-list__heading-row">
        <div className="project-list__heading-main">
          <h2 className="project-list__heading">My Projects</h2>
          {showPublicProjectsLink && (
            <button type="button" className="project-list__public-link" onClick={() => navigate('/explore')}>
              Public Projects →
            </button>
          )}
        </div>
        {totalCount > MAX_VISIBLE && (
          <button type="button" className="project-list__show-more" onClick={() => navigate('/projects')}>
            Show More →
          </button>
        )}
      </div>
      <div className="project-list__grid">
        {projects.map((p, index) => (
          <LandingProjectCard
            key={p.id}
            project={p}
            onClick={(id) => navigate(`/planning/${id}`)}
            autoPlayVideo={index === 1}
            onProjectVisibilityChange={handleProjectVisibilityChange}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectListView: React.FC<ProjectListProps> = ({ isAuthenticated, authIsLoading = false }) => {
  // A stored token means this is a returning user whose own projects belong
  // here, so mount the authenticated view right away rather than after
  // /auth/me resolves. Two things follow from that:
  //   1. one component instance spans the auth-resolution boundary, so the
  //      'My Projects' skeleton is never unmounted and rebuilt (previously the
  //      skeleton's DOM was torn down the moment auth resolved, restarting the
  //      shimmer and flashing the grid);
  //   2. the projects request goes out in parallel with the auth request
  //      instead of queueing behind it, roughly halving how long the skeleton
  //      stays up.
  // It also keeps the heading from jumping "Public Projects" → "My Projects".
  const resolvingReturningUser = authIsLoading && tokenService.hasToken();

  if (isIOSApp()) {
    return isAuthenticated ? <AuthenticatedProjectListView /> : null;
  }

  if (isAuthenticated || resolvingReturningUser) return <AuthenticatedProjectListView />;

  return <PublicProjectListView />;
};

/* Memoised so typing in the landing chat input — which re-renders LandingPage
   on every keystroke — does not re-render three project cards, their screenshot
   stacks and their <video> elements along with it. */
export const ProjectList = React.memo(ProjectListView);
